import { InjectionToken, NgModule } from "@angular/core";
import { ActivatedRouteSnapshot, RouterModule, Routes } from "@angular/router";
import { ADD_ACCOUNT, DASHBOARD_ROUTE, LOGIN_ROUTE, SIGNUP_ROUTE } from "./app-routing.constants";

import { Location } from "@angular/common";
import { LoginContainerComponent } from "./auth/components/login-container/login-container.component";
import { RegisterFormComponent } from "./auth/components/register-form/register-form.component";
import { RegisterPageComponent } from "./auth/components/register-page/register-page.component";
import { AuthGuard } from "./auth/services/authguard.service";
import { MobileReloadComponent } from "./core/components/mobile-reload/mobile-reload.component";
import { NewLandingPageComponent } from "./core/components/new-landing-page/new-landing-page.component";
import { NotFoundComponent } from "./core/components/not-found/not-found.component";
import { PermissionDeniedComponent } from "./core/components/permission-denied/permission-denied.component";
import { DashboardHomeComponent } from "./dashboard/components/home/home.component";
import { DashboardModule } from "./dashboard/dashboard.module";
import { AccountIncompleteComponent } from "./project/components/account-incomplete/account-incomplete.component";
import { OvrcContainerComponent } from "./project/components/ovrc-container/ovrc-container.component";
import { accountRoutePaths } from "./project/project-routing-constants";
import { StaffListComponent } from "./user/components/staff-list/staff-list.component";

export const externalUrlProviderFunction = (route: ActivatedRouteSnapshot) => {
    const externalUrl = route.paramMap.get("externalUrl");
    window.open(externalUrl, "_self");
};

const externalUrlProvider = new InjectionToken("externalUrlRedirectResolver");
export const locationProvider = new InjectionToken<Location>("windowLocationProvider");
export const routes: Routes = [
    {
        path: "myaccount",
        loadChildren: () => import("./customer-portal/customer-portal.module").then(m => m.CustomerPortalModule),
    },
    {
        path: ADD_ACCOUNT,
        component: AccountIncompleteComponent,
        canActivate: [AuthGuard],
        data: {
            permissions: {
                only: ["editProject"],
                redirectTo: accountRoutePaths.root,
            },
        },
    },
    {
        path: "accounts",
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        loadChildren: () => import("./project/project.module").then(m => m.ProjectModule),
        data: {
            permissions: {
                only: ["viewProject", "editProject"],
            },
            title: "Accounts",
        },
    },
    {
        path: "all-staff",
        component: StaffListComponent,
        canActivate: [AuthGuard],
        data: {
            permissions: {
                only: ["editAllCustomers"],
            },
            title: "All Staff",
        },
    },
    {
        path: "entities",
        canActivateChild: [AuthGuard],
        loadChildren: () => import("./entity/entity.module").then(m => m.EntityModule),
        data: {
            permissions: {
                only: ["viewCompany", "editCompany", "updateLockoutScreen"],
            },
            title: "Companies",
        },
    },
    {
        path: LOGIN_ROUTE,
        component: LoginContainerComponent,
        loadChildren: () => import("./auth/auth.module").then(m => m.AuthModule),
        data: {
            title: "Sign In",
        },
    },

    { path: SIGNUP_ROUTE, component: RegisterPageComponent },
    { path: `${SIGNUP_ROUTE}/email`, component: RegisterFormComponent },
    {
        path: "customers",
        canActivateChild: [AuthGuard],
        loadChildren: () => import("./customer/customer.module").then(m => m.CustomerModule),
        data: {
            permissions: {
                only: ["viewCustomerPage", "editCustomerPage"],
            },
            title: "Customers",
        },
    },
    {
        path: "ovrc",
        component: OvrcContainerComponent,
        canActivate: [AuthGuard],
        data: {
            redirectTo: LOGIN_ROUTE,
        },
    },
    {
        path: DASHBOARD_ROUTE,
        canActivate: [AuthGuard],
        component: DashboardHomeComponent,
        data: {
            permissions: {
                except: ["viewCustomerPortal"],
                redirectTo: "/myaccount",
            },
            title: "Home",
        },
    },
    {
        path: "",
        redirectTo: `/${LOGIN_ROUTE}`,
        pathMatch: "full",
    },
    {
        path: "mobile-reload",
        component: MobileReloadComponent,
        data: {
            title: "Connection Interrupted",
        },
    },
    { path: "new-user", component: NewLandingPageComponent },
    {
        path: "permission-denied",
        component: PermissionDeniedComponent,
    },
    // Temporarily comment this out.
    {
        path: "external",
        canActivate: [externalUrlProvider],
        component: NotFoundComponent,
    },
    {
        path: "**",
        component: NotFoundComponent,
    },
];

@NgModule({
    imports: [RouterModule.forRoot(routes, { relativeLinkResolution: "legacy" }), DashboardModule],
    exports: [RouterModule],
    providers: [
        {
            provide: externalUrlProvider,
            useValue: externalUrlProviderFunction,
        },
        {
            provide: locationProvider,
            useValue: window.location,
        },
    ],
})
export class AppRoutingModule {}
